import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Shield, LogOut, Download, Settings, Users, BarChart3, Table2,
  Search, Eye, CheckCircle, AlertCircle, RefreshCw, ChevronRight,
  X, Save, Menu, ExternalLink, Link, Unlink, TrendingUp, PieChart as PieChartIcon,
  FileText, MapPin, Lock, Phone, Clock, Copy, Archive, Trash2, RotateCcw, StickyNote, Volume2
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  viewed: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  claimed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  closed: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

const SHEET_COLUMNS = ["Name","Phone","Email","Location","Case Type","Parties","Incident Date","Status","Documents","Prior Consult","Summary","Contact Pref","Availability","Attorney Type","Category","Lead Status","Created At","ID"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCall, setFilterCall] = useState("");
  const [activeTab, setActiveTab] = useState("leads");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [availableModels, setAvailableModels] = useState({});
  const [configForm, setConfigForm] = useState({ provider: "openai", model: "gpt-4.1-mini", custom_api_key: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [users, setUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "attorney", practice_area: "", location: "" });
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({ threshold: 0.6, silence_duration_ms: 800, prefix_padding_ms: 200, voice: "nova", preset: "chatgpt" });
  const [savingVoice, setSavingVoice] = useState(false);
  const [activePreset, setActivePreset] = useState("chatgpt");
  const [showCustomVAD, setShowCustomVAD] = useState(false);

  const VAD_PRESETS_UI = [
    { id: "chatgpt", label: "ChatGPT-Like", emoji: "⚡", description: "Fast & natural — responds as soon as you pause", settings: { threshold: 0.6, silence_duration_ms: 800, prefix_padding_ms: 200, voice: "nova" } },
    { id: "conservative", label: "Conservative", emoji: "🎯", description: "Waits longer — better for noisy environments", settings: { threshold: 0.85, silence_duration_ms: 2000, prefix_padding_ms: 400, voice: "nova" } },
    { id: "sensitive", label: "Sensitive", emoji: "🔇", description: "Picks up quiet voices — best for elderly users", settings: { threshold: 0.4, silence_duration_ms: 600, prefix_padding_ms: 150, voice: "shimmer" } },
    { id: "custom", label: "Custom", emoji: "🛠️", description: "Set your own values manually", settings: null },
  ];
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [liveStats, setLiveStats] = useState({ active_now: 0, active_voice: 0, total_today: 0, completed_today: 0 });
  const [funnel, setFunnel] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [notifConfig, setNotifConfig] = useState(null);
  const [notifsLoading, setNotifsLoading] = useState(false);
  const [promptData, setPromptData] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [promptSaving, setPromptSaving] = useState(false);
  const [intakeSettings, setIntakeSettings] = useState({ submit_min_phase: 4 });
  const [savingIntake, setSavingIntake] = useState(false);
  const [bulkRepairing, setBulkRepairing] = useState(false);
  const [repairMsg, setRepairMsg] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Dedicated Archived Leads section
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [archLoading, setArchLoading] = useState(false);
  const [archSearch, setArchSearch] = useState("");
  const [archFilterCall, setArchFilterCall] = useState("");
  const [archFilterSource, setArchFilterSource] = useState("");
  const [archFilterScore, setArchFilterScore] = useState("");
  const [archSortDate, setArchSortDate] = useState("newest");
  const [firms, setFirms] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [distLog, setDistLog] = useState([]);
  const [distRules, setDistRules] = useState([]);
  const [distLoading, setDistLoading] = useState(false);
  const [bulkSelectedLeads, setBulkSelectedLeads] = useState([]);
  const [bulkTargetFirm, setBulkTargetFirm] = useState("");
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  const [newPackageFirm, setNewPackageFirm] = useState("");
  const [newPackageLimit, setNewPackageLimit] = useState(10);
  const [newPackageType, setNewPackageType] = useState("basic");
  const [savingPackage, setSavingPackage] = useState(false);
  const [triggeringDist, setTriggeringDist] = useState(false);
  const [topupFirm, setTopupFirm] = useState({});
  const [topupSaving, setTopupSaving] = useState(null);
  const [bulkByType, setBulkByType] = useState("");
  const [bulkByTypeSending, setBulkByTypeSending] = useState(false);
  const [bulkByTypeMsg, setBulkByTypeMsg] = useState(null);

  const getMatchedFirmsForLead = (lead) => {
    if (!firms.length) return [];
    const leadCase = (lead.case_category || lead.case_type || "").toLowerCase();
    const leadLoc = (lead.location || "").toLowerCase();
    return firms.filter(firm => {
      const specs = (firm.specialties || []).map(s => s.toLowerCase());
      const states = (firm.states_licensed || []).map(s => s.toLowerCase());
      const counties = (firm.counties_served || []).map(c => c.toLowerCase());
      const specMatch = !specs.length || specs.some(s => s.includes(leadCase) || leadCase.includes(s));
      const stateMatch = !states.length || states.some(s => leadLoc.includes(s) || s.includes(leadLoc));
      const countyMatch = !counties.length || counties.some(c => leadLoc.includes(c) || c.includes(leadLoc));
      return specMatch && stateMatch && countyMatch && firm.package?.remaining > 0;
    });
  };

  const handleBulkByType = async () => {
    if (!bulkByType) return;
    setBulkByTypeSending(true); setBulkByTypeMsg(null);
    try {
      const res = await axios.post(`${API}/admin/distribution/bulk-by-type`, { case_type: bulkByType }, { headers });
      setBulkByTypeMsg({ type: "success", text: res.data.message });
      fetchAll();
    } catch (e) {
      setBulkByTypeMsg({ type: "error", text: e.response?.data?.detail || "Failed." });
    } finally { setBulkByTypeSending(false); }
  };

  const handleTopup = async (firmId) => {
    const extra = parseInt(topupFirm[firmId] || 0);
    if (!extra || extra <= 0) return;
    setTopupSaving(firmId);
    try {
      const res = await axios.post(`${API}/admin/firms/${firmId}/topup`, { extra_leads: extra }, { headers });
      setSuccessMsg(`Added ${extra} leads to ${res.data.firm_name}. New total: ${res.data.new_limit}`);
      setTopupFirm(prev => ({ ...prev, [firmId]: "" }));
      fetchFirms();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (e) { setError(e.response?.data?.detail || "Top-up failed."); }
    finally { setTopupSaving(null); }
  };
  const [filterTime, setFilterTime] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const user = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const token = localStorage.getItem("adminToken");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAll();
    if (searchParams.get("sheets") === "connected") {
      setSuccessMsg("Google Sheets connected! All leads are now syncing automatically.");
      setActiveTab("spreadsheet");
      setTimeout(() => setSuccessMsg(""), 5000);
    }
    // Poll live stats every 15s
    const fetchLive = () => axios.get(`${API}/admin/live-stats`, { headers }).then(r => setLiveStats(r.data)).catch(() => {});
    fetchLive();
    const interval = setInterval(fetchLive, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { fetchAll(); }, [filterStatus, filterCategory, filterCall, showArchived]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.lead_status = filterStatus;
      if (filterCategory) params.case_category = filterCategory;
      if (filterCall) params.call_status = filterCall;
      if (showArchived) params.archived = true;
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers, params }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setLeads(leadsRes.data.leads);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) { logout(); return; }
      setError("Failed to load data");
    } finally { setLoading(false); }
  };

  const fetchConfig = async () => {
    try {
      const [configRes, promptRes, intakeRes] = await Promise.all([
        axios.get(`${API}/admin/config`, { headers }),
        axios.get(`${API}/admin/config/prompt`, { headers }),
        axios.get(`${API}/admin/intake-settings`, { headers }),
      ]);
      setConfig(configRes.data); setAvailableModels(configRes.data.available_models || {});
      setConfigForm({ provider: configRes.data.provider, model: configRes.data.model, custom_api_key: "" });
      setPromptData(promptRes.data);
      setPromptText(promptRes.data.system_prompt);
      setIntakeSettings(intakeRes.data);
    } catch {}
  };

  const fetchUsers = async () => {
    try { const res = await axios.get(`${API}/admin/users`, { headers }); setUsers(res.data.users); } catch {}
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [analyticsRes, funnelRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/funnel-analytics`, { headers }),
      ]);
      setAnalytics(analyticsRes.data);
      setFunnel(funnelRes.data);
    } catch {}
    finally { setAnalyticsLoading(false); }
  };

  const fetchNotifications = async () => {
    setNotifsLoading(true);
    try {
      const [logsRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/email-logs`, { headers }),
        axios.get(`${API}/admin/notification-config`, { headers }),
      ]);
      setEmailLogs(logsRes.data.logs || []);
      setNotifConfig(configRes.data);
    } catch {} finally { setNotifsLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "config") { fetchConfig(); fetchVoiceSettings(); }
    if (activeTab === "users") fetchUsers();
    if (activeTab === "spreadsheet") { fetchVoiceSettings(); }
    if (activeTab === "analytics") fetchAnalytics();
    if (activeTab === "notifications") fetchNotifications();
    if (activeTab === "firms") { fetchFirms(); fetchDistribution(); fetchDistSettings(); }
    if (activeTab === "archived") fetchArchived();
    if (activeTab === "leads" && firms.length === 0) fetchFirms();
  }, [activeTab]);

  const fetchFirms = async () => {
    setFirmsLoading(true);
    try { const res = await axios.get(`${API}/admin/firms`, { headers }); setFirms(res.data); }
    catch {} finally { setFirmsLoading(false); }
  };

  const handleApproveFirm = async (firmId) => {
    try { await axios.post(`${API}/admin/firms/${firmId}/approve`, {}, { headers }); fetchFirms(); }
    catch {}
  };

  const handleDenyFirm = async (firmId) => {
    if (!window.confirm("Deny and remove this firm registration?")) return;
    try { await axios.post(`${API}/admin/firms/${firmId}/deny`, {}, { headers }); fetchFirms(); }
    catch {}
  };

  const toggleFirmPaused = async (firmId, paused) => {
    try { await axios.put(`${API}/admin/firms/${firmId}`, { paused }, { headers }); fetchFirms(); }
    catch {}
  };

  const [distSettings, setDistSettings] = useState({ auto_distribute: true, default_quota: 10, max_recipients: 3 });
  const fetchDistSettings = async () => {
    try { const res = await axios.get(`${API}/admin/distribution/settings`, { headers }); setDistSettings(res.data); } catch {}
  };
  const saveDistSettings = async (patch) => {
    const next = { ...distSettings, ...patch };
    setDistSettings(next);
    try { await axios.put(`${API}/admin/distribution/settings`, patch, { headers }); } catch {}
  };

  const fetchDistribution = async () => {
    setDistLoading(true);
    try {
      const [logRes, rulesRes] = await Promise.all([
        axios.get(`${API}/admin/distribution/log`, { headers }),
        axios.get(`${API}/admin/distribution-rules`, { headers }),
      ]);
      setDistLog(logRes.data);
      setDistRules(rulesRes.data);
    } catch {} finally { setDistLoading(false); }
  };

  const handleSetPackage = async (firmId) => {
    if (!firmId || !newPackageLimit) return;
    setSavingPackage(true);
    try {
      await axios.post(`${API}/admin/firms/${firmId}/package`, { package_type: newPackageType, leads_limit: Number(newPackageLimit) }, { headers });
      setSuccessMsg("Package set successfully.");
      fetchFirms();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Failed to set package."); }
    finally { setSavingPackage(false); }
  };

  const handleBulkAssign = async () => {
    if (!bulkTargetFirm || bulkSelectedLeads.length === 0) return;
    setBulkSending(true); setBulkMsg(null);
    try {
      const res = await axios.post(`${API}/admin/leads/bulk-assign`, { lead_ids: bulkSelectedLeads, firm_id: bulkTargetFirm }, { headers });
      setBulkMsg(`Assigned ${res.data.assigned} leads. ${res.data.skipped} skipped.`);
      setBulkSelectedLeads([]);
      fetchAll();
    } catch (e) { setBulkMsg(e.response?.data?.detail || "Bulk assign failed."); }
    finally { setBulkSending(false); }
  };

  const handleTriggerDist = async () => {
    setTriggeringDist(true);
    try {
      const res = await axios.post(`${API}/admin/distribution/trigger`, {}, { headers });
      setSuccessMsg(`Auto-distribution triggered for ${res.data.triggered} leads.`);
      fetchDistribution();
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch {} finally { setTriggeringDist(false); }
  };

  const logout = () => { localStorage.removeItem("adminToken"); localStorage.removeItem("adminUser"); navigate("/admin/login"); };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/csv`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError("Export failed"); }
  };

  const bulkRepairData = async () => {
    setBulkRepairing(true);
    setRepairMsg(null);
    try {
      const res = await axios.post(`${API}/admin/leads/bulk-reextract`, {}, { headers });
      const { repaired, total_checked } = res.data;
      setRepairMsg(repaired > 0
        ? `Fixed ${repaired} of ${total_checked} leads with missing names.`
        : `All ${total_checked} leads with missing names were checked — no new data found.`);
      fetchAll();
      setTimeout(() => setRepairMsg(null), 6000);
    } catch (e) {
      setRepairMsg("Repair failed: " + (e.response?.data?.detail || "unknown error"));
    } finally { setBulkRepairing(false); }
  };

  const handleRestoreFromTable = async (leadId) => {
    try {
      await axios.post(`${API}/admin/leads/${leadId}/restore`, {}, { headers });
      fetchAll();
    } catch { setError("Failed to restore lead"); }
  };

  const fetchArchived = async () => {
    setArchLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers, params: { archived: true, limit: 500 } });
      setArchivedLeads(res.data.leads || []);
    } catch { setError("Failed to load archived leads"); }
    finally { setArchLoading(false); }
  };

  const restoreArchived = async (leadId) => {
    try {
      await axios.post(`${API}/admin/leads/${leadId}/restore`, {}, { headers });
      setArchivedLeads(prev => prev.filter(l => l._id !== leadId));
    } catch { setError("Failed to restore lead"); }
  };

  const leadSourceLabel = (l) => {
    const m = l.intake_method;
    if (m === "wizard") return "Manual / Wizard";
    if (m === "chat") return "Sasha Intake";
    return "Sasha Intake";
  };

  const toggleCallStatus = async (leadId, called, e) => {
    if (e) e.stopPropagation();
    const nextVal = !called;
    setLeads(prev => prev.map(l => (l._id === leadId ? { ...l, admin_call: { ...(l.admin_call || {}), called: nextVal } } : l)));
    try { await axios.put(`${API}/admin/leads/${leadId}/call-status`, { called: nextVal }, { headers }); }
    catch { fetchAll(); }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      const currentToken = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/leads/${leadId}`, { headers: { Authorization: `Bearer ${currentToken}` } });
      setDeleteConfirm(null);
      setSuccessMsg("Lead deleted"); setTimeout(() => setSuccessMsg(""), 3000);
      // Refresh leads list without triggering logout on error
      try {
        const params = {};
        if (filterStatus) params.status = filterStatus;
        if (filterCategory) params.category = filterCategory;
        if (showArchived) params.archived = true;
        const [leadsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${currentToken}` }, params }),
          axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${currentToken}` } })
        ]);
        setLeads(leadsRes.data.leads || []);
        setStats(statsRes.data);
      } catch {}
    } catch (e) {
      if (e.response?.status === 401) { logout(); return; }
      setError("Failed to delete lead");
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/admin/config`, { provider: configForm.provider, model: configForm.model, custom_api_key: configForm.custom_api_key || null }, { headers });
      setSuccessMsg("Configuration saved"); setTimeout(() => setSuccessMsg(""), 3000); fetchConfig();
    } catch { setError("Failed to save config"); } finally { setSavingConfig(false); }
  };

  const fetchVoiceSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/voice-settings`, { headers });
      setVoiceSettings(res.data);
      setActivePreset(res.data.preset || "chatgpt");
    } catch {}
  };

  const saveVoiceSettings = async () => {
    setSavingVoice(true);
    try {
      await axios.post(`${API}/admin/voice-settings`, voiceSettings, { headers });
      setSuccessMsg("Voice settings saved"); setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Failed to save voice settings"); }
    finally { setSavingVoice(false); }
  };

  const applyPreset = async (presetId) => {
    const preset = VAD_PRESETS_UI.find(p => p.id === presetId);
    if (!preset) return;
    setActivePreset(presetId);
    if (presetId === "custom") { setShowCustomVAD(true); return; }
    setShowCustomVAD(false);
    const newSettings = { ...preset.settings, preset: presetId };
    setVoiceSettings(newSettings);
    setSavingVoice(true);
    try {
      await axios.post(`${API}/admin/voice-preset`, { preset: presetId }, { headers });
      setSuccessMsg(`Voice preset set to "${preset.label}"`); setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Failed to apply preset"); }
    finally { setSavingVoice(false); }
  };


  const createUser = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers });
      setShowCreateUser(false);
      setNewUser({ email: "", password: "", name: "", role: "attorney", practice_area: "", location: "" });
      fetchUsers(); setSuccessMsg("User created"); setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) { setError(e.response?.data?.detail || "Failed to create user"); }
  };

  const filteredLeads = leads.filter(l => {
    const q = search.toLowerCase();
    const matchesSearch = !q || (l.name||"").toLowerCase().includes(q) || (l.email||"").toLowerCase().includes(q) ||
      (l.case_type||"").toLowerCase().includes(q) || (l.case_category||"").toLowerCase().includes(q);
    if (!matchesSearch) return false;
    // Time filter
    if (filterTime !== "all" && l.created_at) {
      const created = new Date(l.created_at);
      const now = new Date();
      if (filterTime === "today") {
        return created.toDateString() === now.toDateString();
      } else if (filterTime === "week") {
        const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
        return created >= weekAgo;
      } else if (filterTime === "month") {
        const monthAgo = new Date(now); monthAgo.setMonth(now.getMonth() - 1);
        return created >= monthAgo;
      }
    }
    return true;
  });

  const scoreColor = (s) => s >= 70 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-red-400";
  const scoreBg = (s) => s >= 70 ? "bg-emerald-500/10 border-emerald-500/20" : s >= 40 ? "bg-amber-500/10 border-amber-500/20" : "bg-red-500/10 border-red-500/20";

  const navItems = [
    { id: "leads", icon: BarChart3, label: "Leads" },
    { id: "archived", icon: Archive, label: "Archived" },
    { id: "firms", icon: Users, label: "Firms" },
    { id: "analytics", icon: TrendingUp, label: "Analytics" },
    { id: "notifications", icon: AlertCircle, label: "Notifications" },
    { id: "spreadsheet", icon: Table2, label: "Spreadsheet" },
    { id: "config", icon: Settings, label: "AI Config" },
    { id: "security", icon: Lock, label: "Security" },
  ];

  const NavItem = ({ item }) => (
    <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all w-full ${activeTab === item.id ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"}`}
      data-testid={`nav-${item.id}`}>
      <item.icon className="w-4 h-4 flex-shrink-0" /><span>{item.label}</span>
    </button>
  );

  return (
    <div className="bg-background flex" style={{ height: '100dvh' }} data-testid="admin-dashboard">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar - desktop */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-border/30 flex flex-col bg-background transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-testid="sidebar">
        <div className="p-4 sm:p-5 border-b border-border/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Shield className="w-3.5 h-3.5 text-primary" />
            </div>
            <div><div className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>ICantFindALawyer</div><div className="text-xs text-muted-foreground">Admin Portal</div></div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1"><X className="w-4 h-4 text-muted-foreground" /></button>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => <NavItem key={item.id} item={item} />)}
        </nav>
        <div className="p-4 border-t border-border/30 space-y-2">
          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
          <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs border border-primary/20">{user.role}</span>
          <button onClick={logout} className="w-full flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors pt-1" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden border-b border-border/30 bg-background px-4 h-14 flex items-center justify-between flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-secondary/40" data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>{navItems.find(n => n.id === activeTab)?.label}</span>
          <button onClick={exportCSV} className="p-1.5 rounded-lg hover:bg-secondary/40">
            <Download className="w-4 h-4 text-muted-foreground" />
          </button>
        </header>

        {/* Bottom Nav - mobile */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-border/30 bg-background/95 backdrop-blur-xl grid grid-cols-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {navItems.slice(0, 4).map(item => (
            <button key={item.id} onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center gap-1 py-2.5 text-xs transition-all ${activeTab === item.id ? "text-primary" : "text-muted-foreground"}`}
              data-testid={`mobile-nav-${item.id}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px]">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
            {/* Live Activity Bar */}
            <div className="flex items-center gap-3 sm:gap-5 mb-4 sm:mb-6 p-3 rounded-xl bg-card/50 border border-border/30" data-testid="live-stats-bar">
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-2.5 w-2.5">
                  {liveStats.active_now > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${liveStats.active_now > 0 ? "bg-emerald-400" : "bg-muted-foreground/30"}`} />
                </span>
                <span className="text-xs font-medium text-foreground">{liveStats.active_now}</span>
                <span className="text-xs text-muted-foreground">Active Now</span>
              </div>
              {liveStats.active_voice > 0 && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 text-primary" />
                  <span className="text-xs font-medium text-primary">{liveStats.active_voice}</span>
                  <span className="text-xs text-muted-foreground">On Voice</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-xs text-muted-foreground">Today:</span>
                <span className="text-xs font-medium text-foreground">{liveStats.total_today} sessions</span>
                <span className="text-xs text-muted-foreground/60">&middot;</span>
                <span className="text-xs font-medium text-emerald-400">{liveStats.completed_today} completed</span>
              </div>
            </div>

            {error && (
              <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm mb-4 sm:mb-6" data-testid="dashboard-error">
                <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>
                <button onClick={() => setError("")}><X className="w-4 h-4" /></button>
              </div>
            )}
            {successMsg && (
              <div className="flex items-center gap-2 p-3 sm:p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-4 sm:mb-6" data-testid="success-msg">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />{successMsg}
              </div>
            )}

            {/* ── Leads Tab ── */}
            {activeTab === "leads" && (
              <div className="space-y-4 sm:space-y-6" data-testid="leads-tab">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Lead Management</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Client intake submissions</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={fetchAll} className="p-2 rounded-xl border border-border/50 hover:bg-secondary/40 transition-all" data-testid="refresh-btn">
                      <RefreshCw className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button onClick={exportCSV} className="hidden sm:flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl border border-border/50 hover:bg-secondary/40 text-sm transition-all" data-testid="export-csv-btn">
                      <Download className="w-4 h-4" />CSV
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  {[
                    { icon: BarChart3, label: "Total", value: stats.total, color: "bg-primary/10 text-primary" },
                    { icon: AlertCircle, label: "New", value: stats.new, color: "bg-sky-500/10 text-sky-400" },
                    { icon: Eye, label: "Viewed", value: stats.viewed, color: "bg-amber-500/10 text-amber-400" },
                    { icon: CheckCircle, label: "Claimed", value: stats.claimed, color: "bg-emerald-500/10 text-emerald-400" },
                  ].map(({ icon: Icon, label, value, color }) => (
                    <div key={label} className="p-4 rounded-xl border border-border/50 bg-card/30">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground">{label}</span>
                        <div className={`w-7 h-7 rounded-lg ${color} flex items-center justify-center`}><Icon className="w-3.5 h-3.5" /></div>
                      </div>
                      <div className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? "—"}</div>
                    </div>
                  ))}
                </div>

                {/* Attention alerts row */}
                <div className="grid grid-cols-3 gap-3">
                  {stats.stale > 0 && (
                    <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 flex items-center gap-2" data-testid="stale-alert">
                      <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-amber-400">{stats.stale} Needs Attention</p>
                        <p className="text-[10px] text-muted-foreground">New leads &gt; 48 hrs old</p>
                      </div>
                    </div>
                  )}
                  {stats.duplicates > 0 && (
                    <div className="p-3 rounded-xl border border-orange-500/30 bg-orange-500/5 flex items-center gap-2" data-testid="dup-alert">
                      <Copy className="w-4 h-4 text-orange-400 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-orange-400">{stats.duplicates} Duplicates</p>
                        <p className="text-[10px] text-muted-foreground">Same contact info</p>
                      </div>
                    </div>
                  )}
                  {stats.archived > 0 && (
                    <button onClick={() => setShowArchived(v => !v)}
                      className={`p-3 rounded-xl border transition-all flex items-center gap-2 ${showArchived ? "border-primary/40 bg-primary/10" : "border-border/30 bg-card/20 hover:bg-card/40"}`}
                      data-testid="archived-toggle">
                      <Archive className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="text-left">
                        <p className="text-xs font-semibold">{showArchived ? "Active Leads" : `${stats.archived} Archived`}</p>
                        <p className="text-[10px] text-muted-foreground">{showArchived ? "Show active" : "Click to view"}</p>
                      </div>
                    </button>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads..."
                      className="w-full h-10 pl-9 pr-4 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      data-testid="search-input" />
                  </div>
                  <select value={filterTime} onChange={(e) => setFilterTime(e.target.value)}
                    className="h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none cursor-pointer"
                    data-testid="time-filter">
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none cursor-pointer"
                    data-testid="status-filter">
                    <option value="">All Statuses</option>
                    {["new","viewed","claimed","closed"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                  </select>
                  <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                    className="h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none cursor-pointer"
                    data-testid="category-filter">
                    <option value="">All Categories</option>
                    {["Criminal Defense","Personal Injury","Family Law","Employment Law","Immigration","Business Law","Real Estate","Estate Planning","Civil Rights"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={filterCall} onChange={(e) => setFilterCall(e.target.value)}
                    className="h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none cursor-pointer"
                    data-testid="call-filter">
                    <option value="">All Calls</option>
                    <option value="called">Called</option>
                    <option value="not_called">Not Called</option>
                  </select>
                </div>

                {/* Filter results count */}
                {(filterTime !== "all" || filterStatus || filterCategory || search) && (
                  <div className="flex items-center justify-between" data-testid="filter-results-count">
                    <p className="text-xs text-muted-foreground">
                      Showing <span className="font-semibold text-foreground">{filteredLeads.length}</span> of {leads.length} leads
                      {filterTime !== "all" && <span className="text-primary"> · {filterTime === "today" ? "Today" : filterTime === "week" ? "This Week" : "This Month"}</span>}
                    </p>
                    <button onClick={() => { setFilterTime("all"); setFilterStatus(""); setFilterCategory(""); setSearch(""); }}
                      className="text-xs text-primary hover:underline" data-testid="clear-filters-btn">Clear filters</button>
                  </div>
                )}

                {/* Bulk repair banner — shown when leads have missing names */}
                {leads.some(l => !l.name) && (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="bulk-repair-banner">
                    <div>
                      <p className="text-xs font-medium text-amber-400">
                        {leads.filter(l => !l.name).length} lead{leads.filter(l => !l.name).length !== 1 ? "s" : ""} with missing name data
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {repairMsg || "Use 'Fix Data' to auto-repair missing names, phones, and emails from conversation history."}
                      </p>
                    </div>
                    <button onClick={bulkRepairData} disabled={bulkRepairing}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-all text-xs font-medium disabled:opacity-50"
                      data-testid="bulk-repair-btn">
                      <RefreshCw className={`w-3 h-3 ${bulkRepairing ? "animate-spin" : ""}`} />
                      {bulkRepairing ? "Repairing..." : "Fix Data"}
                    </button>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="loading-leads">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Loading leads...
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground" data-testid="no-leads">
                    <BarChart3 className="w-8 h-8 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No leads found</p>
                    <p className="text-xs mt-1">Completed intakes appear here</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile card view */}
                    <div className="sm:hidden space-y-3" data-testid="leads-cards">
                      {filteredLeads.map(lead => (
                        <div key={lead._id} onClick={() => navigate(`/admin/leads/${lead._id}`)}
                          className="p-4 rounded-xl border border-border/50 bg-card/30 active:bg-card/50 transition-all cursor-pointer"
                          data-testid={`lead-card-${lead._id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium text-sm">{lead.name || "Anonymous"}</div>
                              <div className="text-xs text-muted-foreground">{lead.email || "No email"}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={e => toggleCallStatus(lead._id, lead.admin_call?.called, e)}
                                className={`px-2 py-0.5 rounded-full text-xs font-semibold border transition ${lead.admin_call?.called ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/10 text-gray-400 border-gray-500/20"}`}>
                                {lead.admin_call?.called ? "✓ Called" : "Not Called"}
                              </button>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[lead.lead_status] || statusColors.new}`}>
                                {lead.lead_status || "new"}
                              </span>
                              <button onClick={e => { e.stopPropagation(); setDeleteConfirm(lead); }}
                                className="p-1 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all"
                                data-testid={`delete-lead-mobile-${lead._id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{lead.case_category || lead.case_type || "—"}</span>
                            {lead.location && <span>· {lead.location}</span>}
                            {lead.case_score?.overall_score != null && (
                              <span className={`ml-auto font-semibold ${scoreColor(lead.case_score.overall_score)}`}>
                                {lead.case_score.overall_score}/100
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Desktop table view */}
                    <div className="hidden sm:block rounded-xl border border-border/50 overflow-hidden" data-testid="leads-table">
                      <table className="w-full">
                        <thead className="border-b border-border/30 bg-secondary/20">
                          <tr>{["Client","Case Type","Location","Status","Call","Type","Matched Firms","Score","Date",""].map(h => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>
                          ))}</tr>
                        </thead>
                        <tbody className="divide-y divide-border/20">
                          {filteredLeads.map(lead => (
                            <tr key={lead._id} onClick={() => navigate(`/admin/leads/${lead._id}`)}
                              className={`hover:bg-secondary/20 transition-colors cursor-pointer ${lead.is_stale ? "border-l-2 border-l-amber-500/50" : ""}`}
                              data-testid={`lead-row-${lead._id}`}>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{lead.name || "Anonymous"}</span>
                                  {lead.is_duplicate && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20" title="Duplicate submission">DUP</span>
                                  )}
                                  {lead.is_stale && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-0.5">
                                      <Clock className="w-2.5 h-2.5" />Stale
                                    </span>
                                  )}
                                  {lead.notes?.length > 0 && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 flex items-center gap-0.5">
                                      <StickyNote className="w-2.5 h-2.5" />{lead.notes.length}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground mt-0.5">{lead.email || "—"}</div>
                              </td>
                              <td className="px-4 py-4 text-sm">{lead.case_category || lead.case_type || "—"}</td>
                              <td className="px-4 py-4 text-sm text-muted-foreground">{lead.location || "—"}</td>
                              <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                                {(() => {
                                  const matched = getMatchedFirmsForLead(lead);
                                  if (!matched.length) return <span className="text-xs text-muted-foreground italic">No match</span>;
                                  const top = matched.slice(0, 2);
                                  return (
                                    <div className="space-y-1">
                                      <span className="text-[10px] font-bold text-emerald-400">{matched.length} firm{matched.length !== 1 ? "s" : ""} matched</span>
                                      {top.map(f => (
                                        <div key={f.id} className="flex items-center gap-1">
                                          <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${f.tier === "enterprise" ? "bg-amber-500/10 text-amber-400 border-amber-500/20" : f.tier === "pro" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}`}>{f.tier?.slice(0,3).toUpperCase()}</span>
                                          <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{f.firm_name}</span>
                                        </div>
                                      ))}
                                      {matched.length > 2 && <span className="text-[10px] text-muted-foreground">+{matched.length - 2} more</span>}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-4"><span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[lead.lead_status] || statusColors.new}`}>{lead.lead_status || "new"}</span></td>
                              <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                                <button onClick={e => toggleCallStatus(lead._id, lead.admin_call?.called, e)}
                                  className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border transition ${lead.admin_call?.called ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-500/10 text-gray-400 border-gray-500/20"}`}>
                                  {lead.admin_call?.called ? "✓ Called" : "Not Called"}
                                </button>
                              </td>
                              <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={async () => {
                                    const newType = (lead.lead_type || "shared") === "shared" ? "exclusive" : "shared";
                                    await axios.post(`${API}/admin/leads/${lead._id}/type`, { lead_type: newType, max_recipients: 3 }, { headers });
                                    fetchAll();
                                  }}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition hover:opacity-80 ${lead.lead_type === "exclusive" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-sky-500/10 text-sky-400 border-sky-500/20"}`}
                                  title="Click to toggle shared/exclusive"
                                >
                                  {lead.lead_type === "exclusive" ? "EXCLUSIVE" : "SHARED"}
                                </button>
                              </td>
                              <td className="px-4 py-4">
                                {lead.case_score?.overall_score != null ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${scoreBg(lead.case_score.overall_score)}`}>
                                    <span className={scoreColor(lead.case_score.overall_score)}>{lead.case_score.overall_score}</span>
                                    <span className="text-muted-foreground/60">/100</span>
                                  </span>
                                ) : <span className="text-xs text-muted-foreground">—</span>}
                              </td>
                              <td className="px-4 py-4 text-xs text-muted-foreground">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-1">
                                  {showArchived ? (
                                    <button onClick={e => { e.stopPropagation(); handleRestoreFromTable(lead._id); }}
                                      className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-emerald-400 transition-all" title="Restore">
                                      <RotateCcw className="w-3.5 h-3.5" />
                                    </button>
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <button onClick={e => { e.stopPropagation(); setDeleteConfirm(lead); }}
                                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all" title="Delete"
                                    data-testid={`delete-lead-${lead._id}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Archived Leads (dedicated section) ── */}
            {activeTab === "archived" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Archive className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Archived Leads</h2>
                  <span className="text-xs text-muted-foreground">— restore any lead with its notes, scores & call history intact</span>
                </div>

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input value={archSearch} onChange={e => setArchSearch(e.target.value)}
                      placeholder="Search archived leads..."
                      className="w-full pl-8 pr-3 py-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <select value={archSortDate} onChange={e => setArchSortDate(e.target.value)}
                    className="py-2 px-2 rounded-lg bg-background border border-border text-xs">
                    <option value="newest">Date Archived: Newest</option>
                    <option value="oldest">Date Archived: Oldest</option>
                  </select>
                  <select value={archFilterScore} onChange={e => setArchFilterScore(e.target.value)}
                    className="py-2 px-2 rounded-lg bg-background border border-border text-xs">
                    <option value="">All Scores</option>
                    <option value="high">High (70+)</option>
                    <option value="medium">Medium (40–69)</option>
                    <option value="low">Low (&lt;40)</option>
                  </select>
                  <select value={archFilterCall} onChange={e => setArchFilterCall(e.target.value)}
                    className="py-2 px-2 rounded-lg bg-background border border-border text-xs">
                    <option value="">All Calls</option>
                    <option value="called">Called</option>
                    <option value="not_called">Not Called</option>
                  </select>
                  <select value={archFilterSource} onChange={e => setArchFilterSource(e.target.value)}
                    className="py-2 px-2 rounded-lg bg-background border border-border text-xs">
                    <option value="">All Sources</option>
                    <option value="chat">Sasha Intake</option>
                    <option value="wizard">Manual / Wizard</option>
                  </select>
                  <button onClick={fetchArchived} className="p-2 rounded-lg border border-border hover:bg-muted transition text-muted-foreground">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>

                {archLoading ? (
                  <div className="p-12 text-center text-muted-foreground text-sm">Loading archived leads...</div>
                ) : (() => {
                  let list = archivedLeads.filter(l => {
                    const q = archSearch.toLowerCase();
                    const matchSearch = !q || [l.name, l.case_category, l.case_type, l.location, l.email, l.phone]
                      .some(v => v?.toLowerCase().includes(q));
                    const score = l.case_score?.overall_score;
                    const matchScore = !archFilterScore ||
                      (archFilterScore === "high" && score >= 70) ||
                      (archFilterScore === "medium" && score >= 40 && score < 70) ||
                      (archFilterScore === "low" && score != null && score < 40);
                    const called = !!l.admin_call?.called;
                    const matchCall = !archFilterCall || (archFilterCall === "called" ? called : !called);
                    const src = l.intake_method === "wizard" ? "wizard" : "chat";
                    const matchSource = !archFilterSource || src === archFilterSource;
                    return matchSearch && matchScore && matchCall && matchSource;
                  });
                  list = list.sort((a, b) => {
                    const da = a.archived_at || a.created_at || "";
                    const db_ = b.archived_at || b.created_at || "";
                    return archSortDate === "newest" ? db_.localeCompare(da) : da.localeCompare(db_);
                  });
                  if (list.length === 0) {
                    return (
                      <div className="p-12 text-center rounded-xl border border-dashed border-border bg-card/30">
                        <Archive className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground">
                          {archivedLeads.length === 0 ? "No archived leads." : "No archived leads match your filters."}
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                      <div className="px-4 py-2.5 border-b border-border text-xs text-muted-foreground">
                        {list.length} archived lead{list.length !== 1 ? "s" : ""}
                      </div>
                      <div className="divide-y divide-border">
                        {list.map(lead => {
                          const score = lead.case_score?.overall_score;
                          const isCalled = !!lead.admin_call?.called;
                          return (
                            <div key={lead._id} className="flex items-center gap-3 p-4 hover:bg-muted/30 transition">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-sm font-medium truncate">{lead.name || "Anonymous"}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">{leadSourceLabel(lead)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                  <span>{lead.case_category || lead.case_type || "Unknown case"}</span>
                                  {lead.location && <span>· {lead.location}</span>}
                                  {lead.archived_at && <span>· Archived {new Date(lead.archived_at).toLocaleDateString()}</span>}
                                </div>
                              </div>
                              {score != null && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${scoreBg(score)}`}>
                                  <span className={scoreColor(score)}>{score}</span>
                                </span>
                              )}
                              <span className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${isCalled ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-gray-500/10 text-gray-400 border-gray-500/20"}`}>
                                <Phone className="w-3 h-3" />{isCalled ? "Called" : "Not Called"}
                              </span>
                              <button onClick={() => restoreArchived(lead._id)}
                                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition flex-shrink-0">
                                <RotateCcw className="w-3.5 h-3.5" /> Restore
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Firms & Distribution (combined) ── */}
            {activeTab === "firms" && (
              <div className="space-y-6">

                {/* Header */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1">
                    <h2 className="text-base font-semibold">Firms & Distribution</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Manage partner firms, lead packages, and send leads</p>
                  </div>
                  <button onClick={handleTriggerDist} disabled={triggeringDist}
                    className="flex items-center gap-1.5 py-1.5 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-50">
                    <RefreshCw className={`w-3.5 h-3.5 ${triggeringDist ? "animate-spin" : ""}`} />
                    {triggeringDist ? "Running..." : "Run Auto-Distribute"}
                  </button>
                  <button onClick={() => { fetchFirms(); fetchDistribution(); }} className="p-1.5 rounded-lg border border-border hover:bg-muted transition text-muted-foreground">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>

                {successMsg && <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">{successMsg}</div>}

                {/* Auto-Distribute Settings — the network control panel */}
                <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold">Auto-Distribution</p>
                      <p className="text-xs text-muted-foreground mt-0.5">When ON, new leads flow automatically to matching firms in your network — no manual sending.</p>
                    </div>
                    <button onClick={() => saveDistSettings({ auto_distribute: !distSettings.auto_distribute })}
                      className={`relative w-12 h-6 rounded-full transition ${distSettings.auto_distribute ? "bg-emerald-500" : "bg-muted"}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${distSettings.auto_distribute ? "left-6" : "left-0.5"}`} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground block mb-1">Default quota for new firms</label>
                      <input type="number" min="0" value={distSettings.default_quota}
                        onChange={e => setDistSettings(s => ({ ...s, default_quota: e.target.value }))}
                        onBlur={e => saveDistSettings({ default_quota: Number(e.target.value) })}
                        className="w-full py-2 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none" />
                      <p className="text-[11px] text-muted-foreground/70 mt-1">Leads each firm can receive after approval.</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground block mb-1">Max firms per lead</label>
                      <input type="number" min="1" value={distSettings.max_recipients}
                        onChange={e => setDistSettings(s => ({ ...s, max_recipients: e.target.value }))}
                        onBlur={e => saveDistSettings({ max_recipients: Number(e.target.value) })}
                        className="w-full py-2 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none" />
                      <p className="text-[11px] text-muted-foreground/70 mt-1">How many firms a shared lead can go to.</p>
                    </div>
                  </div>
                </div>

                {/* Pending Registrations — accept / deny */}
                {firms.filter(f => f.status === "pending").length > 0 && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-4">
                    <p className="text-sm font-semibold text-amber-300 mb-3">Pending Registrations ({firms.filter(f => f.status === "pending").length})</p>
                    <div className="space-y-2">
                      {firms.filter(f => f.status === "pending").map(firm => (
                        <div key={firm.id} className="flex items-center gap-3 flex-wrap p-3 rounded-lg bg-card border border-border">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">{firm.firm_name || firm.name}</p>
                            <p className="text-xs text-muted-foreground">{firm.email}{firm.specialties?.length ? " · " + firm.specialties.join(", ") : ""}</p>
                          </div>
                          <button onClick={() => handleApproveFirm(firm.id)}
                            className="py-1.5 px-4 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-400 transition">✓ Accept</button>
                          <button onClick={() => handleDenyFirm(firm.id)}
                            className="py-1.5 px-4 rounded-lg border border-destructive/30 text-destructive text-xs font-bold hover:bg-destructive/10 transition">✕ Deny</button>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground/70 mt-2">Accept grants the default quota and starts auto-distribution to this firm.</p>
                  </div>
                )}

                {/* Bulk Send by Case Type */}
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs font-semibold mb-3">Bulk Send by Case Type</p>
                  <div className="flex gap-3 flex-wrap items-center">
                    <select value={bulkByType} onChange={e => setBulkByType(e.target.value)}
                      className="flex-1 py-2 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none">
                      <option value="">Pick a case type to send...</option>
                      {["Personal Injury","Criminal Defense","Family Law","Employment Law","Immigration","Business Law","Real Estate","Estate Planning","Civil Rights","Workers Compensation","Medical Malpractice","Other"].map(ct => (
                        <option key={ct} value={ct}>{ct}</option>
                      ))}
                    </select>
                    <button onClick={handleBulkByType} disabled={!bulkByType || bulkByTypeSending}
                      className="py-2 px-5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50">
                      {bulkByTypeSending ? "Sending..." : "Send All Matched"}
                    </button>
                  </div>
                  {bulkByTypeMsg && (
                    <p className={`text-xs mt-2 ${bulkByTypeMsg.type === "success" ? "text-emerald-400" : "text-destructive"}`}>
                      {bulkByTypeMsg.text}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">Sends all unassigned leads of this case type to every firm whose profile matches. Respects tier priority and package balance.</p>
                </div>

                {/* Active firm cards — each firm IS its own distribution target */}
                <p className="text-sm font-semibold">Active Firms ({firms.filter(f => f.status !== "pending").length})</p>
                {firmsLoading ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">Loading firms...</div>
                ) : firms.filter(f => f.status !== "pending").length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                    No active firms yet. Firms self-register at /attorney/signup, then you approve them above.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {firms.filter(f => f.status !== "pending").map(firm => {
                      const pkg = firm.package;
                      const pct = pkg?.limit ? Math.round((pkg.used / pkg.limit) * 100) : 0;
                      const unassigned = leads.filter(l => !l.assigned_to && !l.archived);
                      const isBulkTarget = bulkTargetFirm === firm.id;
                      return (
                        <div key={firm.id} className="rounded-xl border border-border bg-card overflow-hidden">
                          {/* Firm header row */}
                          <div className="flex items-center gap-4 p-4 border-b border-border">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-sm">{firm.firm_name || firm.name}</p>
                                {firm.status === "pending" ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-400 border-amber-500/30">PENDING</span>
                                ) : (
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${firm.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-gray-500/10 text-gray-400 border-gray-500/20"}`}>
                                    {firm.active ? "ACTIVE" : "INACTIVE"}
                                  </span>
                                )}
                                {firm.tier && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded border bg-muted text-muted-foreground border-border capitalize">{firm.tier}</span>}
                                {firm.status === "pending" && (
                                  <button onClick={() => handleApproveFirm(firm.id)}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500 text-white hover:bg-emerald-400 transition">
                                    ✓ Approve
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{firm.email} · {firm.specialties?.join(", ") || "No specialties set"}</p>
                            </div>
                            {/* Per-firm auto-receive ON/OFF */}
                            {firm.status !== "pending" && (
                              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                <button onClick={() => toggleFirmPaused(firm.id, !firm.paused)} title={firm.paused ? "Paused — not receiving leads" : "Receiving leads"}
                                  className={`relative w-11 h-6 rounded-full transition ${!firm.paused ? "bg-emerald-500" : "bg-muted"}`}>
                                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${!firm.paused ? "left-[22px]" : "left-0.5"}`} />
                                </button>
                                <span className={`text-[9px] font-semibold ${!firm.paused ? "text-emerald-400" : "text-muted-foreground"}`}>{!firm.paused ? "RECEIVING" : "PAUSED"}</span>
                              </div>
                            )}
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-bold">{firm.total_leads_assigned}</p>
                              <p className="text-xs text-muted-foreground">leads received</p>
                            </div>
                          </div>

                          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Package status */}
                            <div>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Lead Package</p>
                              {pkg?.limit ? (
                                <div>
                                  <div className="flex justify-between text-xs mb-1.5">
                                    <span className="capitalize font-medium">{pkg.type} — {pkg.limit} leads</span>
                                    <span className={pct >= 80 ? "text-amber-400 font-semibold" : "text-muted-foreground"}>{pkg.remaining} remaining</span>
                                  </div>
                                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${pct >= 80 ? "bg-amber-400" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                                  </div>
                                  {pct >= 80 && <p className="text-[11px] text-amber-400 mt-1">Package almost full — consider upselling</p>}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">No active package</p>
                              )}
                              {/* Quick package setter */}
                              <div className="flex gap-2 mt-3">
                                <select
                                  value={newPackageFirm === firm.id ? newPackageType : "basic"}
                                  onChange={e => { setNewPackageFirm(firm.id); setNewPackageType(e.target.value); setNewPackageLimit(e.target.value === "pro" ? 25 : e.target.value === "enterprise" ? 50 : 10); }}
                                  className="flex-1 py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none"
                                >
                                  <option value="basic">Basic (10)</option>
                                  <option value="pro">Pro (25)</option>
                                  <option value="enterprise">Enterprise (50)</option>
                                </select>
                                <button
                                  onClick={() => { setNewPackageFirm(firm.id); handleSetPackage(firm.id); }}
                                  disabled={savingPackage}
                                  className="py-1.5 px-3 rounded-lg bg-muted hover:bg-muted/80 border border-border text-xs font-medium transition disabled:opacity-50"
                                >
                                  {savingPackage && newPackageFirm === firm.id ? "..." : "Set Plan"}
                                </button>
                              </div>
                              {/* Top-up extra leads */}
                              {pkg?.limit && (
                                <div className="flex gap-2 mt-2">
                                  <input
                                    type="number" min={1} placeholder="+ extra leads"
                                    value={topupFirm[firm.id] || ""}
                                    onChange={e => setTopupFirm(prev => ({ ...prev, [firm.id]: e.target.value }))}
                                    className="flex-1 py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none"
                                  />
                                  <button
                                    onClick={() => handleTopup(firm.id)}
                                    disabled={topupSaving === firm.id || !topupFirm[firm.id]}
                                    className="py-1.5 px-3 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-primary text-xs font-medium transition disabled:opacity-50"
                                  >
                                    {topupSaving === firm.id ? "..." : "Top Up"}
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Firm distribution profile config */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lead Distribution Profile</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">Auto</span>
                                  <button
                                    onClick={async () => {
                                      await axios.put(`${API}/admin/firms/${firm.id}/profile`, { auto_distribution: !firm.auto_distribution }, { headers });
                                      fetchFirms();
                                    }}
                                    className={`relative w-10 h-5 rounded-full transition-colors ${firm.auto_distribution ? "bg-primary" : "bg-muted border border-border"}`}
                                  >
                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${firm.auto_distribution ? "translate-x-5" : "translate-x-0.5"}`} />
                                  </button>
                                </div>
                              </div>

                              {/* Case types this firm covers */}
                              <div className="mb-3">
                                <p className="text-[10px] text-muted-foreground mb-1.5">Case Types Covered</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {["Personal Injury","Criminal Defense","Family Law","Employment Law","Immigration","Business Law","Real Estate","Estate Planning","Civil Rights","Workers Compensation","Medical Malpractice"].map(ct => {
                                    const active = firm.specialties?.includes(ct);
                                    return (
                                      <button key={ct}
                                        onClick={async () => {
                                          const updated = active
                                            ? (firm.specialties || []).filter(s => s !== ct)
                                            : [...(firm.specialties || []), ct];
                                          await axios.put(`${API}/admin/firms/${firm.id}/profile`, { specialties: updated }, { headers });
                                          fetchFirms();
                                        }}
                                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition ${active ? "bg-primary/15 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border hover:border-primary/30"}`}
                                      >
                                        {ct}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* States + Counties */}
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <p className="text-[10px] text-muted-foreground mb-1">States (comma separated)</p>
                                  <input
                                    defaultValue={firm.states_licensed?.join(", ") || ""}
                                    onBlur={async e => {
                                      const states = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                                      await axios.put(`${API}/admin/firms/${firm.id}/profile`, { states_licensed: states }, { headers });
                                      fetchFirms();
                                    }}
                                    placeholder="Texas, California..."
                                    className="w-full py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                                  />
                                </div>
                                <div>
                                  <p className="text-[10px] text-muted-foreground mb-1">Cities / Counties (comma separated)</p>
                                  <input
                                    defaultValue={firm.counties_served?.join(", ") || ""}
                                    onBlur={async e => {
                                      const counties = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                                      await axios.put(`${API}/admin/firms/${firm.id}/profile`, { counties_served: counties }, { headers });
                                      fetchFirms();
                                    }}
                                    placeholder="Dallas, Houston, Austin..."
                                    className="w-full py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                                  />
                                </div>
                              </div>

                              {/* Priority badge */}
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-[10px] text-muted-foreground">Distribution Priority:</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${firm.tier === "enterprise" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : firm.tier === "pro" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}`}>
                                  {firm.tier === "enterprise" ? "TIER 1 — FIRST PRIORITY" : firm.tier === "pro" ? "TIER 2 — STANDARD" : "TIER 3 — BASIC"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Distribution Log */}
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="text-sm font-semibold">Distribution Log</h3>
                    <span className="text-xs text-muted-foreground">{distLog.length} entries</span>
                  </div>
                  {distLoading ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">Loading...</div>
                  ) : distLog.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No distributions yet — send leads to a firm above to get started.</div>
                  ) : (
                    <div className="divide-y divide-border max-h-80 overflow-y-auto">
                      {distLog.map(entry => (
                        <div key={entry.id} className="flex items-center gap-4 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{entry.firm_name}</p>
                            <p className="text-xs text-muted-foreground">Lead #{entry.lead_id?.slice(-8)}</p>
                          </div>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${entry.method === "auto" ? "bg-violet-500/10 text-violet-400 border-violet-500/20" : entry.method === "bulk" ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-amber-500/10 text-amber-400 border-amber-500/20"}`}>
                            {entry.method}
                          </span>
                          <span className="text-xs text-muted-foreground">{new Date(entry.distributed_at).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* ── Analytics Tab ── */}
            {activeTab === "analytics" && (
              <div className="space-y-4 sm:space-y-6" data-testid="analytics-tab">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Analytics</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Insights across all leads</p>
                  </div>
                  <button onClick={fetchAnalytics} className="p-2 rounded-xl border border-border/50 hover:bg-secondary/40 transition-all">
                    <RefreshCw className={`w-4 h-4 text-muted-foreground ${analyticsLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>

                {analyticsLoading && !analytics ? (
                  <div className="text-center py-16 text-muted-foreground">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Loading analytics...
                  </div>
                ) : analytics ? (
                  <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: "Total Leads", value: analytics.summary.total, icon: BarChart3, color: "text-primary bg-primary/10" },
                        { label: "This Month", value: analytics.summary.this_month, icon: TrendingUp, color: "text-sky-400 bg-sky-500/10" },
                        { label: "Claimed", value: analytics.summary.claimed, icon: CheckCircle, color: "text-emerald-400 bg-emerald-500/10" },
                        { label: "Claim Rate", value: `${analytics.summary.claim_rate}%`, icon: PieChartIcon, color: "text-amber-400 bg-amber-500/10" },
                      ].map(({ label, value, icon: Icon, color }) => (
                        <div key={label} className="p-4 rounded-xl border border-border/50 bg-card/30">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground">{label}</span>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-3.5 h-3.5" /></div>
                          </div>
                          <div className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? "—"}</div>
                        </div>
                      ))}
                    </div>

                    {/* Leads Over Time */}
                    <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                      <h2 className="text-sm font-semibold mb-4">Leads Over Time <span className="text-muted-foreground font-normal">(last 30 days)</span></h2>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={analytics.leads_by_day} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                          <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} tickFormatter={(v) => v.slice(5)}
                            interval={Math.floor(analytics.leads_by_day.length / 6)} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} allowDecimals={false} />
                          <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px", fontSize: "12px" }}
                            labelStyle={{ color: "#94a3b8" }} itemStyle={{ color: "#a5b4fc" }} />
                          <Area type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} fill="url(#colorLeads)" name="Leads" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Category + Status side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Category Breakdown */}
                      <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                        <h2 className="text-sm font-semibold mb-4">Case Category Breakdown</h2>
                        {analytics.category_breakdown.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={analytics.category_breakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                                dataKey="value" nameKey="name" paddingAngle={3}>
                                {analytics.category_breakdown.map((_, idx) => (
                                  <Cell key={idx} fill={["#6366f1","#38bdf8","#34d399","#f59e0b","#f87171","#a78bfa","#fb923c","#4ade80","#e879f9"][idx % 9]} />
                                ))}
                              </Pie>
                              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px", fontSize: "12px" }}
                                itemStyle={{ color: "#e2e8f0" }} />
                              <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: "11px", color: "#94a3b8" }} />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>

                      {/* Status Distribution */}
                      <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                        <h2 className="text-sm font-semibold mb-4">Status Distribution</h2>
                        {analytics.status_distribution.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={analytics.status_distribution} layout="vertical" margin={{ top: 0, right: 5, left: 10, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" horizontal={false} />
                              <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} allowDecimals={false} />
                              <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={55} />
                              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px", fontSize: "12px" }}
                                itemStyle={{ color: "#e2e8f0" }} />
                              <Bar dataKey="value" name="Leads" radius={[0, 4, 4, 0]}>
                                {analytics.status_distribution.map((entry, idx) => {
                                  const colors = { New: "#38bdf8", Viewed: "#f59e0b", Claimed: "#34d399", Closed: "#6b7280" };
                                  return <Cell key={idx} fill={colors[entry.name] || "#6366f1"} />;
                                })}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* Top Locations */}
                    {analytics.top_locations.length > 0 && (
                      <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                        <div className="flex items-center gap-2 mb-4">
                          <MapPin className="w-4 h-4 text-primary/60" />
                          <h2 className="text-sm font-semibold">Top Locations</h2>
                        </div>
                        <div className="space-y-2">
                          {analytics.top_locations.map((loc, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground w-4">{i + 1}</span>
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm">{loc.location}</span>
                                  <span className="text-xs text-muted-foreground">{loc.count} lead{loc.count !== 1 ? "s" : ""}</span>
                                </div>
                                <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
                                  <div className="h-full bg-primary/60 rounded-full transition-all"
                                    style={{ width: `${Math.round((loc.count / analytics.top_locations[0].count) * 100)}%` }} />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <TrendingUp className="w-8 h-8 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No analytics data yet</p>
                    <p className="text-xs mt-1">Analytics appear after leads are submitted</p>
                  </div>
                )}

                {/* ── Conversation Funnel ── */}
                {funnel && (
                  <div className="space-y-4">
                    <div className="border-t border-border/30 pt-4">
                      <h2 className="text-sm font-semibold mb-1">Conversation Funnel</h2>
                      <p className="text-xs text-muted-foreground">Where do users drop off during intake?</p>
                    </div>
                    {/* Funnel KPIs */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: "Started", value: funnel.summary.total_started },
                        { label: "Completed", value: funnel.summary.total_completed, color: "text-emerald-400" },
                        { label: "Abandoned", value: funnel.summary.total_abandoned, color: "text-red-400" },
                        { label: "Completion Rate", value: `${funnel.summary.completion_rate}%`, color: funnel.summary.completion_rate >= 50 ? "text-emerald-400" : "text-amber-400" },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="p-3 rounded-xl border border-border/50 bg-card/20 text-center">
                          <div className={`text-2xl font-bold ${color || ""}`}>{value ?? "—"}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
                        </div>
                      ))}
                    </div>
                    {/* Avg messages */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl border border-border/50 bg-card/20">
                        <p className="text-xs text-muted-foreground mb-1">Avg messages to complete</p>
                        <p className="text-xl font-bold text-emerald-400">{funnel.summary.avg_msgs_to_complete ?? "—"}</p>
                      </div>
                      <div className="p-3 rounded-xl border border-border/50 bg-card/20">
                        <p className="text-xs text-muted-foreground mb-1">Avg messages before abandon</p>
                        <p className="text-xl font-bold text-red-400">{funnel.summary.avg_msgs_before_abandon ?? "—"}</p>
                      </div>
                    </div>
                    {/* Drop-off distribution chart */}
                    {funnel.drop_distribution?.length > 0 && (
                      <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                        <h3 className="text-sm font-semibold mb-4">Drop-off by Message Number</h3>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={funnel.drop_distribution} margin={{ top: 0, right: 5, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff08" />
                            <XAxis dataKey="at_message" tick={{ fill: "#64748b", fontSize: 10 }} label={{ value: "Message #", position: "insideBottom", offset: -2, fill: "#64748b", fontSize: 10 }} />
                            <YAxis tick={{ fill: "#64748b", fontSize: 10 }} allowDecimals={false} />
                            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "8px", fontSize: "12px" }}
                              labelFormatter={(v) => `Abandoned at message ${v}`} itemStyle={{ color: "#fca5a5" }} />
                            <Bar dataKey="count" name="Users abandoned" fill="#f87171" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {/* Top abandon questions */}
                    {funnel.top_abandon_questions?.length > 0 && (
                      <div className="p-4 sm:p-5 rounded-xl border border-border/50 bg-card/20">
                        <h3 className="text-sm font-semibold mb-3">Most Common Last Questions Before Abandonment</h3>
                        <div className="space-y-2">
                          {funnel.top_abandon_questions.map((q, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <span className="text-xs text-muted-foreground w-5 flex-shrink-0 pt-0.5">{i + 1}.</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-foreground/80 leading-relaxed truncate" title={q.question}>{q.question}</p>
                                <div className="h-1 bg-secondary/40 rounded-full mt-1 overflow-hidden">
                                  <div className="h-full bg-red-400/60 rounded-full"
                                    style={{ width: `${Math.round((q.count / funnel.top_abandon_questions[0].count) * 100)}%` }} />
                                </div>
                              </div>
                              <span className="text-xs text-red-400/80 flex-shrink-0">{q.count}×</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground/60 mt-3">💡 Use this data to refine the AI script — these questions cause the most drop-offs.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Notifications Tab ── */}
            {activeTab === "notifications" && (
              <div className="space-y-4 sm:space-y-6" data-testid="notifications-tab">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">Notifications</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Email notification logs & configuration</p>
                  </div>
                  <button onClick={fetchNotifications} className="p-2 rounded-xl border border-border/50 hover:bg-secondary/40 transition-all">
                    <RefreshCw className={`w-4 h-4 text-muted-foreground ${notifsLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>

                {/* Config status banner */}
                {notifConfig && (
                  <div className={`p-4 rounded-xl border flex items-start gap-3 ${notifConfig.resend_configured ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${notifConfig.resend_configured ? "bg-emerald-500/10" : "bg-amber-500/10"}`}>
                      <AlertCircle className={`w-4 h-4 ${notifConfig.resend_configured ? "text-emerald-400" : "text-amber-400"}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${notifConfig.resend_configured ? "text-emerald-400" : "text-amber-400"}`}>
                        {notifConfig.resend_configured ? "Email delivery active" : "Email delivery not configured"}
                      </p>
                      {notifConfig.resend_configured ? (
                        <p className="text-xs text-muted-foreground">Sending from: {notifConfig.sender_email}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {notifConfig.total_queued > 0 && <span className="text-amber-400 font-medium">{notifConfig.total_queued} emails queued. </span>}
                          Add <code className="bg-secondary px-1 rounded text-xs">RESEND_API_KEY</code> to <code className="bg-secondary px-1 rounded text-xs">backend/.env</code> to start sending.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Email log stages guide */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Submitted to Site", color: "bg-sky-500/10 text-sky-400 border-sky-500/20", event: "new" },
                    { label: "Reviewed by Admin", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", event: "viewed" },
                    { label: "Sent to Legal Team", color: "bg-violet-500/10 text-violet-400 border-violet-500/20", event: "claimed" },
                    { label: "Case Closed", color: "bg-gray-500/10 text-gray-400 border-gray-500/20", event: "closed" },
                  ].map(({ label, color, event }) => {
                    const count = emailLogs.filter(l => l.event_type?.includes(event)).length;
                    return (
                      <div key={event} className="p-3 rounded-xl border border-border/40 bg-card/20">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${color} mb-2`}>{label}</span>
                        <p className="text-lg font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground">emails logged</p>
                      </div>
                    );
                  })}
                </div>

                {/* Log list */}
                <div className="rounded-xl border border-border/50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border/30 bg-secondary/20 flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent Email Log</span>
                    <span className="text-xs text-muted-foreground">{emailLogs.length} entries</span>
                  </div>
                  {notifsLoading ? (
                    <div className="py-10 text-center text-muted-foreground text-sm">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />Loading...
                    </div>
                  ) : emailLogs.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground">
                      <AlertCircle className="w-7 h-7 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No emails logged yet</p>
                      <p className="text-xs mt-1">Email logs appear when leads change status</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border/20">
                      {emailLogs.map((log) => (
                        <div key={log.log_id} className="px-4 py-3 flex items-center gap-3 hover:bg-secondary/10 transition-all">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.delivered ? "bg-emerald-400" : "bg-amber-400"}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{log.subject}</p>
                            <p className="text-xs text-muted-foreground truncate">{log.to_email} · {log.event_type}</p>
                            {log.error && <p className="text-xs text-amber-400/80 truncate">{log.error}</p>}
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0 gap-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${log.delivered ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                              {log.delivered ? "Sent" : "Queued"}
                            </span>
                            <span className="text-xs text-muted-foreground">{log.sent_at ? new Date(log.sent_at).toLocaleDateString() : "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Spreadsheet Tab ── */}            {activeTab === "spreadsheet" && (
              <div className="space-y-4 sm:space-y-6" data-testid="spreadsheet-tab">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Spreadsheet</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">All lead data in spreadsheet format</p>
                  </div>
                  <button onClick={exportCSV} className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl border border-border/50 hover:bg-secondary/40 text-sm transition-all" data-testid="sheet-export-btn">
                    <Download className="w-4 h-4" /><span className="hidden sm:inline">Export CSV</span>
                  </button>
                </div>

                {/* Data Encryption Notice */}
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="encryption-notice">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-400">AES-256 Encryption Active</p>
                      <p className="text-xs text-muted-foreground">All personal data (names, phone, email, case details) is encrypted at rest. No external data sharing.</p>
                    </div>
                  </div>
                </div>

                {/* Sasha Voice Mode settings moved to the AI Config tab */}

                {/* In-app Spreadsheet View */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">All Leads Data</h2>
                    <span className="text-xs text-muted-foreground">{leads.length} records</span>
                  </div>
                  {leads.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground border border-border/30 rounded-xl">
                      <Table2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No data yet</p>
                      <p className="text-xs mt-1">Completed intakes will appear here</p>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-border/50 overflow-x-auto" data-testid="spreadsheet-view">
                      <table className="w-full text-xs">
                        <thead className="border-b border-border/30 bg-secondary/30">
                          <tr>
                            {SHEET_COLUMNS.map(col => (
                              <th key={col} className="px-3 py-2.5 text-left font-semibold text-muted-foreground whitespace-nowrap min-w-[100px]">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border/20">
                          {leads.map(lead => (
                            <tr key={lead._id} onClick={() => navigate(`/admin/leads/${lead._id}`)}
                              className="hover:bg-secondary/20 cursor-pointer transition-colors" data-testid={`sheet-row-${lead._id}`}>
                              {[lead.name, lead.phone, lead.email, lead.location, lead.case_type,
                                lead.parties_involved, lead.incident_date, lead.current_status,
                                lead.documents, lead.prior_consultation,
                                lead.fact_summary ? lead.fact_summary.slice(0, 60) + (lead.fact_summary.length > 60 ? "…" : "") : "",
                                lead.contact_preference, lead.availability,
                                lead.recommended_attorney_type, lead.case_category,
                                lead.lead_status, lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "",
                                (lead._id || "").slice(-8)
                              ].map((val, ci) => (
                                <td key={ci} className="px-3 py-2 text-foreground/80 whitespace-nowrap max-w-[180px] truncate">
                                  {val || <span className="text-muted-foreground/40">—</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Config Tab ── */}
            {activeTab === "config" && (
              <div className="space-y-4 sm:space-y-6 max-w-xl" data-testid="config-tab">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Configuration</h1>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Configure the AI model and Sasha's voice mode</p>
                </div>

                {/* Sasha Voice Mode — preset cards + custom */}
                <div className="rounded-xl border border-border/50 bg-card/20 p-4 sm:p-5 space-y-4" data-testid="voice-settings-panel">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                      <Volume2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Sasha Voice Mode</p>
                      <p className="text-xs text-muted-foreground">Pick a preset or go custom — applies instantly to all new voice sessions</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {VAD_PRESETS_UI.map(preset => {
                      const isActive = activePreset === preset.id;
                      return (
                        <button key={preset.id} onClick={() => applyPreset(preset.id)} disabled={savingVoice}
                          className={`relative flex flex-col items-center text-center p-3 rounded-xl border transition-all ${isActive ? "border-primary bg-primary/10 text-primary" : "border-border bg-card/30 text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}>
                          {isActive && <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><span className="text-[9px] text-primary-foreground font-bold">✓</span></div>}
                          <span className="text-2xl mb-1.5">{preset.emoji}</span>
                          <p className="text-xs font-semibold leading-tight">{preset.label}</p>
                          <p className="text-[10px] mt-1 leading-snug opacity-70">{preset.description}</p>
                        </button>
                      );
                    })}
                  </div>
                  {activePreset !== "custom" && (
                    <div className="flex items-center gap-4 px-3 py-2 rounded-lg bg-muted/30 text-xs text-muted-foreground">
                      <span>Threshold: <strong className="text-foreground">{voiceSettings.threshold}</strong></span>
                      <span>Silence: <strong className="text-foreground">{voiceSettings.silence_duration_ms}ms</strong></span>
                      <span>Voice: <strong className="text-foreground capitalize">{voiceSettings.voice || "nova"}</strong></span>
                    </div>
                  )}
                  {showCustomVAD && (
                    <div className="space-y-4 pt-2 border-t border-border/30">
                      <p className="text-xs font-medium text-muted-foreground">Custom Configuration</p>
                      <div>
                        <div className="flex justify-between mb-1"><label className="text-xs text-muted-foreground">Pickup Threshold</label><span className="text-xs text-primary font-bold">{voiceSettings.threshold}</span></div>
                        <input type="range" min="0.1" max="1.0" step="0.05" value={voiceSettings.threshold}
                          onChange={e => setVoiceSettings(v => ({ ...v, threshold: parseFloat(e.target.value) }))}
                          className="w-full h-2 rounded-full appearance-none bg-secondary/40 accent-primary cursor-pointer" />
                        <div className="flex justify-between text-[10px] text-muted-foreground/50 mt-1"><span>Sensitive</span><span>Strict</span></div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1"><label className="text-xs text-muted-foreground">Silence Duration</label><span className="text-xs text-primary font-bold">{voiceSettings.silence_duration_ms}ms</span></div>
                        <input type="range" min="500" max="5000" step="100" value={voiceSettings.silence_duration_ms}
                          onChange={e => setVoiceSettings(v => ({ ...v, silence_duration_ms: parseInt(e.target.value) }))}
                          className="w-full h-2 rounded-full appearance-none bg-secondary/40 accent-primary cursor-pointer" />
                        <div className="flex justify-between text-[10px] text-muted-foreground/50 mt-1"><span>Fast (500ms)</span><span>Slow (5s)</span></div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1"><label className="text-xs text-muted-foreground">Voice</label></div>
                        <select value={voiceSettings.voice || "nova"} onChange={e => setVoiceSettings(v => ({ ...v, voice: e.target.value }))}
                          className="w-full py-2 px-3 rounded-lg bg-background border border-border text-sm focus:outline-none">
                          <option value="nova">Nova — warm, conversational (recommended)</option>
                          <option value="shimmer">Shimmer — soft, gentle (elderly-friendly)</option>
                          <option value="alloy">Alloy — neutral</option>
                          <option value="echo">Echo — natural male</option>
                          <option value="fable">Fable — expressive</option>
                          <option value="onyx">Onyx — deep, authoritative</option>
                        </select>
                      </div>
                      <button onClick={saveVoiceSettings} disabled={savingVoice}
                        className="w-full py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium transition-all disabled:opacity-50">
                        {savingVoice ? "Saving..." : "Save Custom Settings"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-5 sm:p-6 rounded-2xl border border-border/50 bg-card/30 space-y-4 sm:space-y-5">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">AI Provider</label>
                    <select value={configForm.provider} onChange={(e) => {
                      const p = e.target.value; const models = availableModels[p] || [];
                      setConfigForm({ ...configForm, provider: p, model: models[0] || "" });
                    }} className="w-full h-11 px-3 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer" data-testid="provider-select">
                      {Object.keys(availableModels).map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Model</label>
                    <select value={configForm.model} onChange={(e) => setConfigForm({ ...configForm, model: e.target.value })}
                      className="w-full h-11 px-3 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer" data-testid="model-select">
                      {(availableModels[configForm.provider] || []).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Custom API Key <span className="text-muted-foreground font-normal">(optional)</span></label>
                    <input type="password" value={configForm.custom_api_key} onChange={(e) => setConfigForm({ ...configForm, custom_api_key: e.target.value })}
                      placeholder={config?.has_custom_key ? "Custom key set (leave blank to keep)" : "Enter your API key"}
                    className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" data-testid="api-key-input" />
                    <p className="text-xs text-muted-foreground">Leave blank to use the configured default key.</p>
                  </div>
                  <button onClick={saveConfig} disabled={savingConfig}
                    className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    data-testid="save-config-btn">
                    <Save className="w-4 h-4" />{savingConfig ? "Saving..." : "Save Configuration"}
                  </button>
                </div>

                {/* ── AI Script / Prompt Editor ── */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-base font-semibold">Intake Conversation Script</h2>
                      <p className="text-xs text-muted-foreground mt-0.5">Control exactly what Sasha says and how the conversation flows</p>
                    </div>
                    {promptData?.is_custom && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">Custom</span>
                    )}
                  </div>

                  {/* Conversation strategy guide */}
                  <div className="p-3.5 rounded-xl border border-border/40 bg-secondary/15 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Current Intake Flow</p>
                    <div className="grid grid-cols-1 gap-1.5">
                      {[
                        { phase: "Phase 1", label: "Contact Info", desc: "Full legal name, phone, email — confirmed via card", color: "text-sky-400 bg-sky-500/10 border-sky-500/20" },
                        { phase: "Phase 2", label: "Location", desc: "City & state of the incident", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
                        { phase: "Phase 3", label: "Incident Details", desc: "What happened + 3 strategic follow-up questions", color: "text-violet-400 bg-violet-500/10 border-violet-500/20" },
                        { phase: "Phase 4", label: "Call Preference", desc: "Best time for attorney to call", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
                        { phase: "Phase 5", label: "Final Review", desc: "Full summary overlay — confirm & submit", color: "text-primary bg-primary/10 border-primary/20" },
                      ].map(({ phase, label, desc, color }) => (
                        <div key={phase} className="flex items-start gap-2.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs border font-medium flex-shrink-0 mt-0.5 ${color}`}>{phase}</span>
                          <div>
                            <span className="text-xs font-medium text-foreground/80">{label} — </span>
                            <span className="text-xs text-muted-foreground">{desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Prompt textarea */}
                  <div className="relative">
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      rows={16}
                      className="w-full px-4 py-3 rounded-xl bg-secondary/30 border border-border/50 text-xs sm:text-sm text-foreground/90 font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all"
                      placeholder="Enter your custom system prompt here..."
                      data-testid="prompt-editor"
                      spellCheck={false}
                    />
                    <div className="absolute bottom-3 right-3 text-xs text-muted-foreground/50">
                      {promptText.length} chars
                    </div>
                  </div>

                  {/* Prompt action buttons */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={async () => {
                        setPromptSaving(true);
                        try {
                          await axios.put(`${API}/admin/config/prompt`, { system_prompt: promptText }, { headers });
                          setSuccessMsg("Conversation script saved! Changes apply to new sessions immediately."); setTimeout(() => setSuccessMsg(""), 4000);
                          await fetchConfig();
                        } catch { setError("Failed to save prompt."); }
                        finally { setPromptSaving(false); }
                      }}
                      disabled={promptSaving}
                      className="flex-1 h-10 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                      data-testid="save-prompt-btn">
                      <Save className="w-3.5 h-3.5" />
                      {promptSaving ? "Saving..." : "Save Script"}
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Reset to the default conversation script?")) return;
                        setPromptSaving(true);
                        try {
                          await axios.put(`${API}/admin/config/prompt`, { system_prompt: "" }, { headers });
                          setSuccessMsg("Script reset to default."); setTimeout(() => setSuccessMsg(""), 3000);
                          await fetchConfig();
                        } catch {}
                        finally { setPromptSaving(false); }
                      }}
                      className="sm:w-auto px-4 h-10 rounded-full border border-border/50 text-muted-foreground text-sm hover:bg-secondary/40 transition-all flex items-center justify-center gap-2"
                      data-testid="reset-prompt-btn">
                      <RefreshCw className="w-3.5 h-3.5" />Reset to Default
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground/60">
                    Changes take effect immediately for all new conversations.
                  </p>
                </div>

                {/* ── Submit Button Timing Control ── */}
                <div className="rounded-xl border border-border/50 bg-card/20 p-4 sm:p-5 space-y-4" data-testid="submit-timing-panel">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Submit Button Timing</p>
                      <p className="text-xs text-muted-foreground">Control when the submit/summary card becomes available to the client</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-medium mb-2 block">Show submit card when reaching:</label>
                    <select
                      value={intakeSettings.submit_min_phase}
                      onChange={e => setIntakeSettings(s => ({ ...s, submit_min_phase: parseInt(e.target.value) }))}
                      className="w-full h-11 px-4 rounded-xl bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                      data-testid="submit-phase-select">
                      <option value={0}>Phase 1 — Contact Info (earliest)</option>
                      <option value={1}>Phase 2 — Location</option>
                      <option value={2}>Phase 3 — Incident Details</option>
                      <option value={3}>Phase 4 — Call Preference</option>
                      <option value={4}>Phase 5 — Final Review (default)</option>
                    </select>
                    <p className="text-xs text-muted-foreground/60 mt-1.5">Even when allowed, the submit card only appears when Sasha outputs [INTAKE_READY] in the prompt.</p>
                  </div>
                  <button
                    onClick={async () => {
                      setSavingIntake(true);
                      try {
                        await axios.post(`${API}/admin/intake-settings`, intakeSettings, { headers });
                        setSuccessMsg("Submit timing saved!"); setTimeout(() => setSuccessMsg(""), 3000);
                      } catch { setError("Failed to save intake settings."); }
                      finally { setSavingIntake(false); }
                    }}
                    disabled={savingIntake}
                    className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-all disabled:opacity-50"
                    data-testid="save-intake-settings-btn">
                    {savingIntake ? "Saving..." : "Save Submit Timing"}
                  </button>
                </div>
              </div>
            )}


            {/* ── Security Tab (Change Password) ── */}
            {activeTab === "security" && (
              <div className="space-y-4 sm:space-y-6" data-testid="security-tab">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Security</h1>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Change your admin password</p>
                </div>
                <div className="bg-card border border-border/40 rounded-xl p-4 sm:p-6 max-w-md">
                  <h2 className="text-base font-semibold mb-4">Change Password</h2>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Current Password</label>
                      <input type="password" value={pwForm.current_password}
                        onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="Enter current password" data-testid="current-password-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">New Password</label>
                      <input type="password" value={pwForm.new_password}
                        onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="Min 8 characters" data-testid="new-password-input" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Confirm New Password</label>
                      <input type="password" value={pwForm.confirm_password}
                        onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="Re-enter new password" data-testid="confirm-password-input" />
                    </div>
                    <button
                      disabled={pwSaving || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password}
                      onClick={async () => {
                        if (pwForm.new_password !== pwForm.confirm_password) { setError("Passwords don't match"); return; }
                        if (pwForm.new_password.length < 8) { setError("New password must be at least 8 characters"); return; }
                        setPwSaving(true); setError("");
                        try {
                          const token = localStorage.getItem("token");
                          await axios.post(`${API}/auth/change-password`, {
                            current_password: pwForm.current_password,
                            new_password: pwForm.new_password
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          setSuccessMsg("Password changed successfully");
                          setPwForm({ current_password: "", new_password: "", confirm_password: "" });
                        } catch (e) {
                          setError(e.response?.data?.detail || "Failed to change password");
                        } finally { setPwSaving(false); }
                      }}
                      className="w-full px-4 py-2.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="change-password-btn">
                      {pwSaving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><Lock className="w-4 h-4" />Change Password</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Users Tab ── */}
            {activeTab === "users" && (
              <div className="space-y-4 sm:space-y-6" data-testid="users-tab">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>User Management</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Admin and attorney accounts</p>
                  </div>
                  <button onClick={() => setShowCreateUser(true)} className="px-3 sm:px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-all" data-testid="create-user-btn">
                    Add User
                  </button>
                </div>

                {showCreateUser && (
                  <div className="p-4 sm:p-6 rounded-2xl border border-primary/20 bg-card/40 space-y-4" data-testid="create-user-form">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Create New User</h3>
                      <button onClick={() => setShowCreateUser(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {[
                        { key: "name", label: "Full Name", type: "text", placeholder: "John Smith" },
                        { key: "email", label: "Email", type: "email", placeholder: "attorney@example.com" },
                        { key: "password", label: "Password", type: "password", placeholder: "••••••••" },
                        { key: "practice_area", label: "Practice Area", type: "text", placeholder: "Criminal Defense" },
                        { key: "location", label: "Location", type: "text", placeholder: "New York, NY" },
                      ].map(({ key, label, type, placeholder }) => (
                        <div key={key} className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">{label}</label>
                          <input type={type} value={newUser[key]} onChange={(e) => setNewUser({ ...newUser, [key]: e.target.value })}
                            placeholder={placeholder} className="w-full h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                            data-testid={`new-user-${key}`} />
                        </div>
                      ))}
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-muted-foreground">Role</label>
                        <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                          className="w-full h-10 px-3 rounded-lg bg-secondary/40 border border-border/50 text-sm focus:outline-none cursor-pointer" data-testid="new-user-role">
                          <option value="attorney">Attorney</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <button onClick={createUser} className="px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-medium transition-all" data-testid="submit-create-user-btn">
                      Create User
                    </button>
                  </div>
                )}

                {/* Mobile card view */}
                <div className="sm:hidden space-y-3" data-testid="users-cards">
                  {users.map(u => (
                    <div key={u._id} className="p-4 rounded-xl border border-border/50 bg-card/20" data-testid={`user-card-${u._id}`}>
                      <div className="flex items-start justify-between">
                        <div><p className="font-medium text-sm">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${u.role === 'admin' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-secondary text-muted-foreground border-border/50'}`}>{u.role}</span>
                      </div>
                      {(u.practice_area || u.location) && (
                        <p className="text-xs text-muted-foreground mt-2">{[u.practice_area, u.location].filter(Boolean).join(" · ")}</p>
                      )}
                      {u.role !== "admin" && <button onClick={() => { if(window.confirm("Delete?")) axios.delete(`${API}/admin/users/${u._id}`,{headers}).then(fetchUsers); }} className="text-xs text-destructive mt-2">Remove</button>}
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No users found</p>}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block rounded-xl border border-border/50 overflow-hidden" data-testid="users-table">
                  <table className="w-full">
                    <thead className="border-b border-border/30 bg-secondary/20">
                      <tr>{["Name","Email","Role","Practice Area","Location",""].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-border/20">
                      {users.map(u => (
                        <tr key={u._id} className="hover:bg-secondary/20 transition-colors" data-testid={`user-row-${u._id}`}>
                          <td className="px-4 py-4 font-medium text-sm">{u.name}</td>
                          <td className="px-4 py-4 text-sm text-muted-foreground">{u.email}</td>
                          <td className="px-4 py-4"><span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${u.role === 'admin' ? 'bg-primary/10 text-primary border-primary/20' : 'bg-secondary text-muted-foreground border-border/50'}`}>{u.role}</span></td>
                          <td className="px-4 py-4 text-sm text-muted-foreground">{u.practice_area || "—"}</td>
                          <td className="px-4 py-4 text-sm text-muted-foreground">{u.location || "—"}</td>
                          <td className="px-4 py-4">{u.role !== "admin" && <button onClick={() => { if(window.confirm("Delete?")) axios.delete(`${API}/admin/users/${u._id}`,{headers}).then(fetchUsers); }} className="text-xs text-destructive" data-testid={`delete-user-${u._id}`}>Remove</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && <p className="text-center py-8 text-sm text-muted-foreground">No users</p>}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-confirm-overlay">
          <div className="bg-background border border-border rounded-2xl p-6 max-w-sm w-full shadow-2xl" data-testid="delete-confirm-dialog">
            <h3 className="text-lg font-semibold text-foreground mb-2">Delete Lead?</h3>
            <p className="text-sm text-muted-foreground mb-1">
              This will permanently delete <strong>{deleteConfirm.name || "this lead"}</strong> and all associated data.
            </p>
            <p className="text-xs text-red-400 mb-6">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-secondary/40 transition-all"
                data-testid="delete-cancel-btn">
                Cancel
              </button>
              <button onClick={() => handleDeleteLead(deleteConfirm._id)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/25 transition-all"
                data-testid="delete-confirm-btn">
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
